import { Link } from "react-router-dom";
import { MapPin, CalendarSync, Flame, ShieldCheck, ArrowRight, ClipboardList, Truck } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, Section, SectionHeading } from "./Section";

export const Hero = () => (
  <section className="relative min-h-[100svh] overflow-hidden lc-grain" data-testid="hero-section">
    <div className="absolute inset-0 lg:left-[42%]">
      <motion.img
        src="/img/hero-stove.jpg"
        alt="A black wood-burning stove with a glowing fire beside neatly stacked kiln-dried logs"
        className="h-full w-full object-cover object-center"
        initial={{ scale: 1.08 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        fetchpriority="high"
      />
      <div className="absolute inset-0 bg-[#080808]/60 lg:bg-transparent lg:bg-[linear-gradient(90deg,#080808_0%,rgba(8,8,8,0.55)_35%,rgba(8,8,8,0)_70%)]" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-[linear-gradient(0deg,#080808,rgba(8,8,8,0))]" aria-hidden="true" />
    </div>
    <div className="relative lc-container flex min-h-[100svh] flex-col justify-center pt-28 pb-16">
      <div className="max-w-2xl">
        <motion.p className="lc-eyebrow" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <span className="h-px w-6 bg-[#D4A437]/70" /> Monthly firewood subscription · Newtownabbey & BT36–BT39
        </motion.p>
        <motion.h1
          className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.95]"
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}
          data-testid="hero-headline"
        >
          Your Firewood.
          <br />
          Delivered. <span className="text-[#FF6A00]">Sorted.</span>
        </motion.h1>
        <motion.p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#F7F5F0]/80"
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.7 }}>
          Premium firewood delivered straight to your door, every month. No last-minute log runs. No hassle. Just a warmer home.
        </motion.p>
        <motion.div className="mt-10 flex flex-col gap-3 sm:flex-row"
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.7 }}>
          <Link to="/plans" className="lc-btn-primary !px-8 !py-4 text-base" data-testid="hero-explore-plans">
            Explore Our Plans <ArrowRight className="h-4 w-4" />
          </Link>
          <a href="#how-it-works" className="lc-btn-ghost !px-8 !py-4 text-base" data-testid="hero-how-it-works">How It Works</a>
        </motion.div>
      </div>
    </div>
  </section>
);

const TRUST = [
  { icon: MapPin, title: "Local Northern Ireland delivery", text: "BT36 · BT37 · BT38 · BT39" },
  { icon: CalendarSync, title: "Flexible monthly subscriptions", text: "Pause, skip or cancel online" },
  { icon: Flame, title: "Kindling & firelighters included", text: "Everything to get it lit" },
  { icon: ShieldCheck, title: "Kiln-dried, ready to burn", text: "Quality logs every month" },
];

export const TrustStrip = () => (
  <div className="border-y border-[#1d1d1d] bg-[#0c0c0c]" data-testid="trust-strip">
    <div className="lc-container grid grid-cols-2 gap-px lg:grid-cols-4">
      {TRUST.map(({ icon: Icon, title, text }, i) => (
        <Reveal key={title} delay={i * 0.06} className="flex items-start gap-3 px-1 py-6 sm:px-4 sm:py-8">
          <Icon className="h-6 w-6 shrink-0 text-[#FF6A00]" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold leading-snug text-[#F7F5F0]">{title}</p>
            <p className="mt-1 text-xs text-[#8a8a8a]">{text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </div>
);

const STEPS = [
  { icon: ClipboardList, title: "Choose your plan.", text: "Pick Starter, Regular or Family — every plan includes kiln-dried logs, kindling and firelighters." },
  { icon: MapPin, title: "Tell us where to deliver.", text: "Check your postcode, add your address and any delivery instructions." },
  { icon: Truck, title: "Enjoy regular firewood deliveries.", text: "Your logs arrive every month. Pause, skip, change or cancel any time from My WeeLogRun." },
];

export const HowItWorksSteps = ({ withHeading = true }) => (
  <Section id="how-it-works" className="scroll-mt-20" data-testid="how-it-works-section">
    {withHeading && <SectionHeading eyebrow="How it works" title="Three steps to a warmer home." intro="Setting up takes a couple of minutes. After that, we take care of the rest." />}
    <ol className="mt-14 grid gap-6 md:grid-cols-3">
      {STEPS.map(({ icon: Icon, title, text }, i) => (
        <Reveal key={title} delay={i * 0.1}>
          <li className="group relative h-full overflow-hidden rounded-3xl border border-[#262626] bg-[#101010] p-8 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#FF6A00]/40" data-testid={`step-${i + 1}`}>
            <span className="absolute -right-2 -top-6 font-display text-[9rem] font-extrabold leading-none text-[#F7F5F0]/[0.04] transition-colors group-hover:text-[#FF6A00]/10" aria-hidden="true">
              {i + 1}
            </span>
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[#FF6A00]/10 text-[#FF6A00]">
              <Icon className="h-6 w-6" />
            </span>
            <p className="mt-8 text-xs font-semibold uppercase tracking-[0.2em] text-[#D4A437]">Step {i + 1}</p>
            <h3 className="mt-2 font-display text-2xl font-bold">{title}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-[#A1A1AA]">{text}</p>
          </li>
        </Reveal>
      ))}
    </ol>
  </Section>
);
